import React from 'react';
import {
    Route,
    Switch,
} from 'react-router-dom';
import PropTypes from 'prop-types';
import { css } from 'styled-components';
import { ErrorBoundary } from 'react-error-boundary';

import COLORS from '@mlb/react-patterns/style/universal.color';
import Theme from '@mlb/react-patterns/components/Theme/theme';

import LandingPage from 'bdvp-landing-page';
import PlaybackPage from 'bdvp-playback-page';
import ReelsPage from 'bdvp-reel/src/containers/ReelsPage';
import SearchPage, { FilterPanel } from 'bdvp-advanced-search';
import GameIndexPage from 'bdvp-game';
import NotFound from 'bdvp-common/src/components/NotFound';
import Redirect from 'bdvp-common/src/components/Redirect';
import ShareModal from 'bdvp-common/src/containers/ShareModal';
import ReelCreationModal from 'bdvp-reel/src/containers/CreationModal';
import { useMediaConfig, useInAppView } from 'bdvp-common/src/hooks/config';

import theme from 'bdvp-common/src/styles/theme';

import Subnav from '../Subnav';
import Bootstrap from '../../containers/Bootstrap';
import Template from '../Template';
import ErrorMsg from '../ErrorMsg';
import MaintenancePage from '../MaintenancePage';

const styles = css`
    body {
        width: 100%;
        height: unset;
        min-height: 100%;
        background-color: ${COLORS.mlbBlack};

        &[class*=league-], &[class*=team-] {
            background-color: ${COLORS.mlbBlack};
        }

        &.using-mouse {
            *:focus {
                outline: none;
            }
        }

        @supports (margin-top: constant(safe-area-inset-top)) {
            padding-top: constant(safe-area-inset-top, 0);
            padding-bottom: constant(safe-area-inset-bottom, 0);
        }

        @supports (margin-top: env(safe-area-inset-top)) {
            padding-top: env(safe-area-inset-top, 0);
            padding-bottom: env(safe-area-inset-bottom, 0);
        }

        main {
            overflow-x: unset !important;
            display: flex;
            align-items: stretch;
            flex-flow: column;
        }
    }

    #mlb-video-app {
        overflow: initial;
        flex: 1;
        display: flex;
        flex-direction: column;
        position: relative;
        // Overriding some global styles from basesite
        [class*="lazyload"] {
            opacity: inherit;
            transition: none;
        }
    }
`;

const getTheme = (theme, showSubnav) => {

    if (!showSubnav) {

        return {
            ...theme,
            subnav: {
                ...theme.subnav,
                height: '0px',
            },
        };

    }

    return theme;

};

const routeComponents = {
    LandingPage,
    GameIndexPage,
    SearchPage,
    PlaybackPage,
    ReelsPage,
    NotFound,
    MaintenancePage,
};

const defaultRoutes = [
    {
        exact: true,
        path: '/',
        component: 'SearchPage',
    },
    {
        path: '/game/:gamePk(\\d+)',
        component: 'GameIndexPage',
    },
    {
        path: '/topic/:topicId',
        component: 'LandingPage',
    },
    {
        path: '/:userId/reels/:contentId',
        component: 'PlaybackPage',
    },
    {
        path: '/:userId/reels',
        component: 'ReelsPage',
    },
    {
        exact: true,
        path: '/:contentId',
        component: 'PlaybackPage',
    },
    {
        component: 'NotFound',
    },
];

const defaultRedirects = [
    {
        from: '/search',
        to: '/',
        withQuery: true,
        status: 301,
    },
];

const App = ({ isFilterActive }) => {

    const {
        routes = defaultRoutes,
        redirects = defaultRedirects,
    } = useMediaConfig();
    const [
        showSubnav = true,
        hasFilterPanel = true,
    ] = useInAppView([
        'subnav',
        'filterPanel',
    ]);

    return (
        <Theme theme={getTheme(theme, showSubnav)} customStyles={styles}>
            <ErrorBoundary FallbackComponent={ErrorMsg} >
                <Bootstrap />
                <Template
                    subnav={showSubnav ? <Subnav isFilterActive={isFilterActive} /> : null}
                    filter={hasFilterPanel ? <FilterPanel /> : null}
                    isFilterActive={isFilterActive && hasFilterPanel}
                    content={
                        <ErrorBoundary FallbackComponent={ErrorMsg} >
                            <Switch>
                                {redirects.map(({ ...props }, index) => (
                                    <Redirect key={index} {...props} />
                                ))}
                                {routes.map(({ component, ...props }, index) => (
                                    <Route key={index} component={routeComponents[component]} {...props} />
                                ))}
                            </Switch>
                        </ErrorBoundary>
                    }
                    modals={
                        <>
                            <ShareModal />
                            <ReelCreationModal />
                        </>
                    }
                />
            </ErrorBoundary>
        </Theme>
    );

};

App.propTypes = {
    isFilterActive: PropTypes.bool,
};

App.defaultProps = {
    isFilterActive: false,
};

export default App;
